import React, { useState, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { MiniSection, LoanSummary, SelectedLoanTerm, AgreementCheckbox } from '../components'
import api from '../api-client'
import { lappToFormData } from './consumerApplicationForm/util'
import { LoanApplication, StatusData, StreetAddress, ChangeOrderStatus, IChangeOrder } from '@oneethos/shared'
import { useAppState } from '../hooks'

const ChangeOrderReviewPage: React.FC = () => {
  const { token } = useParams<{ token: string }>()

  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [changeOrder, setChangeOrder] = useState<IChangeOrder | null>(null)
  const [loanApplication, setLoanApplication] = useState<LoanApplication | null>(null)
  const [installerName, setInstallerName] = useState<string | null>(null)
  const [approving, setApproving] = useState(false)
  const { tenant } = useAppState()

  useEffect(() => {
    if (!token) {
      setError('Missing required parameters')
      setLoading(false)
      return
    }

    fetchChangeOrderData()
  }, [token])

  const handleError = (err: any) => {
    if (err.error) {
      setError(err.error)
    } else if (err.message) {
      setError(err.message)
    } else {
      setError(err.toString())
    }
  }

  const fetchChangeOrderData = async () => {
    try {
      const data = await api.get(`/change-orders/${token}`)
      setChangeOrder(data.changeOrder)
      setLoanApplication(data.lapp)
      setInstallerName(data.installerName)
    } catch (err) {
      handleError(err)
    } finally {
      setLoading(false)
    }
  }

  const handleApprove = async () => {
    if (!changeOrder || !token) return

    setApproving(true)
    try {
      const result = await api.post(`/change-orders/${token}/approve`, changeOrder)
      setChangeOrder(result)
      setApproving(false)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to approve changes')
    } finally {
      setApproving(false)
    }
  }

  if (changeOrder?.status === ChangeOrderStatus.Approved) {
    return (
      <div className="container mt-5">
        <MiniSection type="success">
          <h4>Change Order Approval Received</h4>
          <p>We have received your approval. In most cases you will receive a new Docusign package that reflects your
            updated loan agreement. If you have previously received a Docusign package, you will need to sign the new
            documents.</p>
        </MiniSection>
      </div>
    )
  }

  if (loading) {
    return (
      <div className="container mt-5">
        <div className="text-center">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="container mt-5">
        <MiniSection type="secondary">
          <p>{error}</p>
        </MiniSection>
      </div>
    )
  }

  if (!changeOrder || !loanApplication) {
    return (
      <div className="container mt-5">
        <MiniSection type="warning">
          <h4>Not Found</h4>
          <p>Change order or loan application not found.</p>
        </MiniSection>
      </div>
    )
  }

  const formData = lappToFormData(loanApplication as LoanApplication)
  const project = new StatusData(loanApplication)
  const changeProject = new StatusData({ ...loanApplication, ...changeOrder })

  return (
    <div className="container mt-4 change-order-review-page">
      <div className="row">
        <div className="col-12">
          <h2>Review Proposed Changes</h2>
          <p className="lead">
            Your installer, {installerName}, has submitted a change order for your
            Clean Energy Loan application at {StreetAddress.toString(loanApplication.installationAddress)}.
            Please review the proposed changes to your loan application.
          </p>

          <div className="row">
            <div className="col-md-6">
              <div className="card-secondary p-5 bg-light">
                <h4>Current Loan Summary</h4>
                <LoanSummary
                  formData={formData}
                  showApr={false}
                />
                <h4 className="mt-3">Current System Size</h4>
                <p className="fs-5">{loanApplication.systemSizeKW} kW</p>
                {project.productId ? <>
                  <h4 className="mt-3">Monthly Payment</h4>
                  <SelectedLoanTerm
                    project={project}
                    tenant={tenant}
                    onChange={() => { }}
                  />
                </> : null}
              </div>
            </div>

            <div className="col-md-6">
              <div className="card-secondary p-5">
                <h4>Proposed Changes</h4>
                <LoanSummary
                  formData={changeProject}
                  showApr={false}
                  compareFormData={formData}
                />
                <h4 className="mt-3">New System Size</h4>
                <p className={`fs-5 ${changeOrder.systemSizeKW !== formData.systemSizeKW ? 'text-primary fw-bold' : ''}`}>{changeOrder.systemSizeKW} kW</p>
                {project.productId ? <>
                  <h4 className="mt-3">New Monthly Payment</h4>
                  <SelectedLoanTerm
                    project={changeProject}
                    tenant={tenant}
                    onChange={() => { }}
                  />
                </> : null}
              </div>
            </div>
          </div>

          <div className="card-secondary p-4 mt-3">
            <AgreementCheckbox name="customerAgreesToChangeOrder" formData={changeOrder as any} update={(data, value) => {
              setChangeOrder({
                ...changeOrder,
                customerAgreesToChangeOrder: value
              })
            }}>
              I agree to these changes proposed by my installer, {installerName}. I also understand that these changes
              will require modifications to my Loan Documents. If I have already received a Docusign
              package, I will need to sign new documents that reflect the changes. Additionally, 
              my installer will be notified that I have approved the changes.
            </AgreementCheckbox>
          </div>
          <div className="card-secondary p-4 mt-3 alert alert-info">
            <b>
              If you do not approve of these change or have any questions, please contact your installer directly.
            </b>
          </div>
          <div className="buttons text-center mt-3 mb-5">
            <button
              className="btn btn-success btn-lg me-3"
              onClick={handleApprove}
              disabled={approving || !changeOrder.customerAgreesToChangeOrder}
            >
              {approving ? 'Approving...' : 'Approve Changes'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ChangeOrderReviewPage
